import { useEffect, useState } from "react";
import { Loader2, Search, Link2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

const fmtDay = (s) => (s ? new Date(s).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "");

// Searchable picker used to link an opportunity <-> RFQ. `url` returns [{id, code, title, client_name, created_at, same_client}].
export const LinkPickerDialog = ({ open, onClose, title, url, sameLabel, onPick, testid }) => {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState("");
  useEffect(() => {
    if (!open) { return; }
    setRows(null);
    const t = setTimeout(() => {
      api.get(url, { params: { q } }).then(({ data }) => setRows(data)).catch((e) => { toast.error(apiError(e)); setRows([]); });
    }, 250);
    return () => clearTimeout(t);
  }, [open, url, q]);
  const pick = async (r) => {
    setBusy(r.id);
    try { await onPick(r); onClose(); } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };
  const same = (rows || []).filter((r) => r.same_client);
  const others = (rows || []).filter((r) => !r.same_client);
  const Row = ({ r }) => (
    <button type="button" onClick={() => pick(r)} disabled={!!busy} className="flex w-full items-center gap-3 rounded-lg border border-border px-3 py-2 text-left transition-colors hover:border-[#1B4383] hover:bg-[#F5F8FD]" data-testid={`${testid}-option-${r.id}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2"><span className="font-mono text-xs font-semibold text-[#1B4383]">{r.code}</span><span className="text-[11px] text-muted-foreground">{fmtDay(r.created_at)}</span></div>
        <div className="truncate text-sm font-medium text-slate-800">{r.title || "—"}</div>
        {r.client_name && <div className="truncate text-xs text-muted-foreground">{r.client_name}</div>}
      </div>
      {busy === r.id ? <Loader2 className="h-4 w-4 animate-spin text-[#1B4383]" /> : <Link2 className="h-4 w-4 text-slate-300" />}
    </button>
  );
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid={testid}>
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input autoFocus className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search number, title or client…" data-testid={`${testid}-search`} />
        </div>
        <div className="max-h-[55vh] space-y-3 overflow-y-auto pr-1">
          {rows === null ? <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
            : rows.length === 0 ? <p className="py-6 text-center text-sm text-muted-foreground">Nothing to link.</p> : (<>
              {same.length > 0 && <div className="space-y-1.5"><div className="text-xs font-semibold text-slate-500">{sameLabel}</div>{same.map((r) => <Row key={r.id} r={r} />)}</div>}
              {others.length > 0 && <div className="space-y-1.5"><div className="text-xs font-semibold text-slate-500">{same.length ? "Other Clients" : "All"}</div>{others.map((r) => <Row key={r.id} r={r} />)}</div>}
            </>)}
        </div>
      </DialogContent>
    </Dialog>
  );
};
